<script setup lang="ts">
import { useEventDetail } from '../lib/useEventDetail';
import { useTimepadRegistration } from '../lib/useTimepadRegistration';
import EventDetailHeader from './EventDetailHeader.vue';
import EventDetailHero from './EventDetailHero.vue';
import EventDetailInfo from './EventDetailInfo.vue';
import EventDetailBroadcast from './EventDetailBroadcast.vue';

import EventDetailProgram from './EventDetailProgram.vue';
import EventDetailPartners from './EventDetailPartners.vue';
import EventDetailParticipation from './EventDetailParticipation.vue';
import EventDetailContacts from './EventDetailContacts.vue';
import EventDetailFooter from './EventDetailFooter.vue';
import EventDetailMaterials from './EventDetailMaterials.vue';
import EventDetailTimepadModal from './EventDetailTimepadModal.vue';
import EventDetailRegistrationModal from './EventDetailRegistrationModal.vue';
import { MANUAL_REGISTRATION_ENABLED } from '../lib/manualRegistration';
import { ChronicleBanner2 } from '~/entities/chronicle_banners';
import { EventsCarousel } from '~/widgets/events-carousel';
import { isPastEvent } from '~/shared/lib/getEventRoute';
import { useEventDetailSeo } from '../lib/seo';

const { data, status, error } = await useEventDetail();
const { hasTimepadRegistration, iframeSrcdoc } = useTimepadRegistration(
  computed(() => ({
    timepadId: data.value?.timepad_id,
    timepadScript: data.value?.timepad_script,
  })),
);
const isTimepadLoading = ref(false);
const isTimepadModalVisible = ref(false);
const isRegistrationModalVisible = ref(false);
const isPast = computed(() => Boolean(data.value && isPastEvent(data.value)));
const isPartnerEvent = computed(() => Boolean(data.value?.partner_event));
const hasManualRegistration = computed(
  () =>
    MANUAL_REGISTRATION_ENABLED &&
    Boolean(data.value) &&
    !isPast.value &&
    !isPartnerEvent.value,
);
const hasRegistrationModal = computed(
  () =>
    !isPartnerEvent.value &&
    (hasManualRegistration.value || hasTimepadRegistration.value),
);

watch(
  () => data.value?.id,
  () => {
    isRegistrationModalVisible.value = false;
    isTimepadModalVisible.value = false;
    isTimepadLoading.value = false;
  },
);

// A month-only event has no real date to put into a calendar
const shouldShowCalendarButton = computed(
  () => !isPast.value && !data.value?.month_only,
);

const shouldShowParticipationButton = computed(
  () => Boolean(data.value) && !isPast.value,
);

const isParticipationDisabled = computed(
  () => !hasRegistrationModal.value && !data.value?.url,
);

const handleParticipationClick = (event: Event) => {
  if (isPartnerEvent.value) {
    return;
  }

  if (hasManualRegistration.value) {
    event.preventDefault();
    isRegistrationModalVisible.value = true;
    return;
  }

  if (!hasTimepadRegistration.value) {
    return;
  }

  event.preventDefault();
  isTimepadLoading.value = true;
  isTimepadModalVisible.value = true;
};

const handleTimepadModalClose = () => {
  isTimepadLoading.value = false;
  isTimepadModalVisible.value = false;
};

const handleTimepadMessage = (event: MessageEvent) => {
  const messageData = event.data as
    | {
        source?: string;
        type?: string;
      }
    | undefined;

  if (messageData?.source !== 'cre-timepad') {
    return;
  }

  if (messageData.type === 'timepad-open') {
    isTimepadLoading.value = false;
  }

  if (messageData.type === 'timepad-close') {
    isTimepadLoading.value = false;
    isTimepadModalVisible.value = false;
  }
};

onMounted(() => {
  window.addEventListener('message', handleTimepadMessage);
});

onBeforeUnmount(() => {
  window.removeEventListener('message', handleTimepadMessage);
});

useEventDetailSeo(data);

if (error.value) {
  throw createError({
    status: 404,
    message: 'Мероприятие не найдено',
    fatal: true,
  });
}
</script>

<template>
  <div :class="$style.page">
    <template v-if="data && status === 'success'">
      <EventDetailHeader />
      <EventDetailHero
        :event="data"
        :has-timepad-registration="hasRegistrationModal"
        :is-participation-disabled="isParticipationDisabled"
        :on-participation-click="handleParticipationClick"
        :show-participation-button="shouldShowParticipationButton"
        :show-calendar-button="shouldShowCalendarButton"
      />
      <div :class="$style.content">
        <EventDetailInfo
          :format="data.format"
          :target-audience="data.target_audience"
          :description="data.description"
        >
          <template v-if="data.id === 27" #before-description>
            <EventDetailBroadcast />
          </template>
        </EventDetailInfo>
        <EventDetailProgram :halls="data.halls" />
        <EventDetailPartners
          :partners="data.partner"
          :erid="data.erid"
          :is-past="isPast"
        />
        <EventDetailParticipation
          :intro="data.participation_intro"
          :details="data.participation_details"
        />
        <EventDetailContacts :contacts="data.contact" />
        <EventDetailFooter
          :event-id="data.id"
          :event-url="data.url"
          :event-name="data.name"
          :has-timepad-registration="hasRegistrationModal"
          :is-participation-disabled="isParticipationDisabled"
          :on-participation-click="handleParticipationClick"
          :show-participation-button="shouldShowParticipationButton"
          :show-calendar-button="shouldShowCalendarButton"
        />
        <EventDetailMaterials :related-news="data.related_news" />
      </div>

      <ChronicleBanner2 :class="$style.chronicleBanner" />
      <EventsCarousel :exclude-event-id="data.id" show-past />
      <EventDetailTimepadModal
        v-if="!hasManualRegistration && !isPartnerEvent"
        :is-loading="isTimepadLoading"
        :is-visible="isTimepadModalVisible"
        :iframe-srcdoc="iframeSrcdoc"
        :title="`Регистрация на ${data.name}`"
        @close="handleTimepadModalClose"
      />
      <EventDetailRegistrationModal
        v-if="hasManualRegistration"
        :key="data.id"
        :event-id="data.id"
        :event-name="data.name"
        :is-visible="isRegistrationModalVisible"
        @close="isRegistrationModalVisible = false"
      />
    </template>

    <div v-else-if="status === 'pending'" :class="$style.loader">
      <Loader />
    </div>

    <div v-else-if="status === 'error'" :class="$style.error">
      <p>Не удалось загрузить мероприятие</p>
    </div>
  </div>
</template>

<style module lang="scss">
.page {
  background-color: var(--color-white);
  min-height: calc(100vh - 64px);
}

.content {
  display: flex;
  flex-direction: column;
  padding: var(--space-6x) 0 0;
  gap: var(--space-4x);
}

.page .chronicleBanner {
  border-bottom: none;
}

@media (min-width: 1380px) {
  .content {
    padding-left: 110px;
    padding-right: 110px;
  }
}

.loader {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 64px);
}

.error {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 64px);
  @include text_body1;
  color: var(--color-gray-text);
}
</style>
